import { forwardRef, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Input, type InputProps } from './Input';
import { Tooltip } from './Tooltip';

type PasswordInputProps = Omit<InputProps, 'type' | 'endAdornment'>;

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ label, ...rest }, ref) {
    const { t } = useTranslation('auth');
    const [visible, setVisible] = useState(false);
    const toggleLabel = visible ? t('login.hidePassword') : t('login.showPassword');

    return (
      <Input
        ref={ref}
        label={label}
        type={visible ? 'text' : 'password'}
        endAdornment={
          <Tooltip content={toggleLabel}>
            <button
              type="button"
              onClick={() => setVisible((v) => !v)}
              // The tooltip is a `group-hover` div with no ARIA wiring, so the
              // button's own label is the only thing a screen reader can read.
              aria-label={toggleLabel}
              aria-pressed={visible}
              className="text-ink-muted hover:text-ink p-0.5"
            >
              {visible ? (
                <EyeOff className="size-4" aria-hidden />
              ) : (
                <Eye className="size-4" aria-hidden />
              )}
            </button>
          </Tooltip>
        }
        {...rest}
      />
    );
  },
);
